import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  Divider,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ShareIcon from "@mui/icons-material/Share";
import { DonateButtonVariant } from "../Button/Button";
import { Images } from "../../../utils/Images";
import PatientSlider from "./PatientSlider";
import ShareLink from "../ShareLink";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { validateDonationAmount } from "../../../utils/donationValidation";
import { getCampaignShareDetails } from "../../../api/services/campaignService";
import { getCampaignUrl } from "../../../utils/slugUtils";

const MedicalCampaignHeader = ({ campaignData, donationAmount = 1000, onDonateClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isAbove1200 = useMediaQuery("(min-width:1200px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");

  // Share dialog — same "Share the link" pattern used on LifeDetailPageV2 /
  // ReferFriend, wired here so the Figma share button actually does something.
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const campaignId = campaignData?.id;

  const fetchShareDetails = useCallback(async () => {
    if (!campaignId) return;
    setShareLoading(true);
    setShareError(null);
    try {
      const data = await getCampaignShareDetails(campaignId);
      setShareInfo(data);
    } catch (error) {
      console.error("Failed to fetch share details:", error);
      setShareError("Unable to load share options right now.");
    } finally {
      setShareLoading(false);
    }
  }, [campaignId]);

  const handleShareClick = () => {
    if (!shareInfo && !shareLoading) {
      fetchShareDetails();
    }
    setShowShareDialog(true);
  };

  const handleCloseShareDialog = () => setShowShareDialog(false);

  const fallbackShareUrl =
    (typeof window !== "undefined" && window.location?.origin && campaignData
      ? (() => {
          const url = getCampaignUrl(campaignData);
          return url ? `${window.location.origin}${url}` : "";
        })()
      : "") || "";

  // Extract data from campaignData
  const title = campaignData?.title || "They Wait Patiently With Tears In Their Eyes, For You To Respond";
  const storyDescription = campaignData?.story?.description || "";
  const storyImages = campaignData?.story?.images || [];
  const medicalDetails = campaignData?.medical_details || {};
  const taxBenefit = campaignData?.taxBenefit || "Tax Benefit";

  const hasText = (value) => typeof value === "string" && value.trim().length > 0;

  const beneficiaryName = medicalDetails.beneficiary_name?.trim() || "";
  const campaignerName = medicalDetails.campaigner_name?.trim() || "";
  const campaignerContact = medicalDetails.campaigner_contact?.trim() || "";
  const hospitalAddress = medicalDetails.hospital_address?.trim() || "";
  const hospitalLink = medicalDetails.hospital_link?.trim() || "";
  const timeDuration = medicalDetails.time_duration || "5 Days";
  const isContactHide = medicalDetails.isContactHide === 1;

  const showBeneficiary = hasText(beneficiaryName);
  const showCampaigner = hasText(campaignerName);
  const showHospital = hasText(hospitalAddress);

  // Handle contact button click
  const handleContactClick = () => {
    if (campaignerContact) {
      window.location.href = `tel:${campaignerContact}`;
    }
  };

  // Handle hospital link click
  const handleHospitalLinkClick = () => {
    if (hospitalLink) {
      window.open(hospitalLink, '_blank');
    }
  };

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  // Handle donate button click
  const handleDonateClick = () => {
    // The story page passes onDonateClick to scroll to the on-page donation
    // box (desktop) / open the bottom sheet (mobile) instead of navigating
    // away to /donate-once-page.
    if (onDonateClick) {
      onDonateClick();
      return;
    }
    const amount = donationAmount ?? 1000;
    const validation = validateDonationAmount(amount, cmsHome, false, "₹");
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }
    navigate("/donate-once-page", {
      state: {
        campaignTitle: title || "",
        campaignId: campaignData?.id || null,
        amount,
      },
    });
  };

  return (
    <>
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      bgcolor="#fff"
      borderRadius={3}
      overflow="hidden"
      width="94.44%"
      maxWidth="1440px"
      margin="0 auto"
      marginTop="20px"
      alignItems="stretch"
      minHeight={isMobile ? "auto" : "100%"}
    >
      {/* Left: Patient Slider.
          Width is a FIXED flex-basis, not flex:1 — with flex:1 both columns had
          min-width:auto, so the text column's content-based minimum could steal
          width from this one after mount (web font loading is enough to shift
          it). react-slick bakes slide widths in at mount and only re-measures on
          window resize, so a later shift left stale widths behind and the next
          slide bled into view. 53.1% = the design's 722/1360 (Figma 798:7234). */}
      <Box
        sx={{
          flex: { xs: "1 1 auto", md: "0 0 53.1%" },
          width: { xs: "100%", md: "53.1%" },
          maxWidth: { xs: "100%", md: "53.1%" },
          minWidth: 0,
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PatientSlider storyImages={storyImages} taxBenefit={taxBenefit} />
      </Box>

      {/* Right: Content — Figma (node 798:7234) bottom-aligns the info cards so
          the "Hospitalized at" box ends flush with the image's bottom edge.
          space-between pins the title/description/donate group to the top and
          the cards group to the bottom; on mobile the column height is auto so
          this is a no-op and the content just stacks. */}
      <Box
        sx={{
          flex: "1 1 0%",
          // minWidth:0 stops this column's content-based minimum from
          // redistributing width away from the image column above.
          minWidth: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: isMobile ? "10px" : isAbove1200 ? "30px" : "20px",
          // Design has the text column flush with the top and bottom of the
          // image (title 10px down, hospital card ending on the image's bottom
          // edge), so trim the vertical padding on desktop.
          pt: { md: "10px" },
          pb: { md: 0 },
        }}
      >
        <Box>
        {/* Title + Share button (Figma: black rounded-square icon, top-right) */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "18px" : isAbove1200 ? "36px" : "22px",
              color: "#1D1D1B",
              mb: "16px",
            }}
          >
            {title}
          </Typography>
          {!isMobile && (
            <IconButton
              onClick={handleShareClick}
              sx={{
                flexShrink: 0,
                background: "#1D1D1B",
                width: "46px",
                height: "48px",
                borderRadius: "8px",
                "&:hover": { background: "#000" },
              }}
            >
              <ShareIcon sx={{ fontSize: "18px", color: "#fff" }} />
            </IconButton>
          )}
        </Box>

        {/* Description */}
        {storyDescription && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#666666",
              fontWeight: 400,
              mb: 3,
              "& *": {
                color: "inherit",
              },
            }}
            dangerouslySetInnerHTML={{ __html: storyDescription }}
          />
        )}

        {/* Donate + Time */}
        <Box display="flex" alignItems="center">
          <DonateButtonVariant
            text="Donate Now"
            onClick={handleDonateClick}
            isMobile={isMobile}
            sx={{
              bgcolor: "#F04951",
              color: "#fff",
              width: isMobile ? "100%" : "160px",
              fontSize: isMobile ? "14px" : "16px",
              display: "block",
              mx: { xs: 0, sm: "auto" },
            }}
          />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 3, borderColor: "#B9B9B8" }}
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#EC1B26",
                fontWeight: 800,
                fontSize: isMobile ? "16px" : "20px",
              }}
            >
              {timeDuration}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#4A4A49", fontWeight: 400, fontSize: "12px" }}
            >
              Time remaining
            </Typography>
          </Box>
        </Box>

        </Box>

        {/* ── Bottom group: info cards, flush with the image bottom ── */}
        <Box>
        {/* Beneficiary & Campaigner */}
        {(showBeneficiary || showCampaigner) && (
        <Box display="flex" flexWrap="wrap" gap={2} mb={2} mt={3}>
          {/* Beneficiary */}
          {showBeneficiary && (
          <Card
            sx={{
              flex: 1,
              borderRadius: "12px",
              border: "1px solid #F1F1F1",
              boxShadow: "none",
              minWidth: isMobile ? "100%" : "auto",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: "16px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F0F1F1",
                  borderRadius: "7px",
                }}
              >
                <img
                  src={Images.person}
                  alt="Images.person"
                  width={24}
                  height={24}
                />
              </Box>
              <Box>
                <Typography
                  fontSize="12px"
                  color="#686866"
                  fontWeight={500}
                  mb={0.5}
                >
                  Beneficiary
                </Typography>
                <Typography
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  color="#0B1215"
                  sx={{
                    fontSize: isMobile ? "12px" : "16px",
                  }}
                >
                  {beneficiaryName}{" "}
                  <Tooltip
                    title={
                      <Box sx={{ width: "270px", height: "88px", padding: "10px" }}>
                        <Typography
                          sx={{
                            // fontFamily: "Red Hat Display",
                            fontWeight: 700,
                            fontSize: "12px",
                            lineHeight: "130%",
                            letterSpacing: "-0.01em",
                            color: "#1D1D1B",
                            mb: "8px",
                          }}
                        >
                          Verified By True hope Team at Hospital
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "0.3px solid #B9B9B8",
                            mb: "8px",
                          }}
                        />
                        <Typography
                          sx={{
                            // fontFamily: "Red Hat Display",
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "20px",
                            letterSpacing: "-0.01em",
                            color: "#4A4A49",
                          }}
                        >
                          True hope has verified this campaign through KYC, hospital details & a background check
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#FFFFFF",
                          padding: 0,
                          borderRadius: "5px",
                          width: "270px",
                          height: "88px",
                          boxShadow: "0px 4px 12px 0px #0000001F",
                          "& .MuiTooltip-arrow": {
                            color: "#FFFFFF",
                          },
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.verified}
                      alt="Images.verified"
                      width={16}
                      height={16}
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                </Typography>
              </Box>
            </CardContent>
          </Card>
          )}

          {/* Campaigner */}
          {showCampaigner && (
          <Card
            sx={{
              flex: 1,
              borderRadius: "12px",
              border: "1px solid #F1F1F1",
              boxShadow: "none",
              minWidth: isMobile ? "100%" : "auto",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                p: "16px",
                "&:last-child": { pb: "16px" },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F0F1F1",
                  borderRadius: "7px",
                  padding: "8px",
                }}
              >
                <img
                  src={Images.person}
                  alt="Images.person"
                  width={24}
                  height={24}
                />
              </Box>
              <Box flex={1}>
                <Typography
                  fontSize="12px"
                  color="#686866"
                  fontWeight={500}
                  mb={0.5}
                >
                  Campaigner
                </Typography>
                <Typography
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  color="#0B1215"
                  sx={{
                    fontSize: isMobile ? "12px" : "16px",
                  }}
                >
                  {campaignerName}{" "}
                  <Tooltip
                    title={
                      <Box sx={{ width: "270px", height: "88px", padding: "10px" }}>
                        <Typography
                          sx={{
                            // fontFamily: "Red Hat Display",
                            fontWeight: 700,
                            fontSize: "12px",
                            lineHeight: "130%",
                            letterSpacing: "-0.01em",
                            color: "#1D1D1B",
                            mb: "8px",
                          }}
                        >
                          Verified By True hope Team at Hospital
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "0.3px solid #B9B9B8",
                            mb: "8px",
                          }}
                        />
                        <Typography
                          sx={{
                            // fontFamily: "Red Hat Display",
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "20px",
                            letterSpacing: "-0.01em",
                            color: "#4A4A49",
                          }}
                        >
                          True hope has verified this campaign through KYC, hospital details & a background check
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#FFFFFF",
                          padding: 0,
                          borderRadius: "5px",
                          width: "270px",
                          height: "88px",
                          boxShadow: "0px 4px 12px 0px #0000001F",
                          "& .MuiTooltip-arrow": {
                            color: "#FFFFFF",
                          },
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.verified}
                      alt="Images.verified"
                      width={16}
                      height={16}
                      sx={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                </Typography>
              </Box>
              {isContactHide && campaignerContact && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleContactClick}
                  sx={{
                    background: "#1D1D1B",
                    color: "#fff",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    px: "12px",
                    py: "8px",
                    borderRadius: "8px",
                    "&:hover": { background: "#000" },
                  }}
                >
                  <img
                    src={Images.phone}
                    alt="Images.phone"
                    width={16}
                    height={16}
                  />
                  Contact
                </Button>
              )}
            </CardContent>
          </Card>
          )}
        </Box>
        )}

        {/* Hospital Info — only when address is present (link alone is not enough) */}
        {showHospital && (
        <Card
          sx={{
            borderRadius: "12px",
            border: "1px solid #F1F1F1",
            boxShadow: "none",
            mt: 2,
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: "16px",
              "&:last-child": { pb: "16px" },
            }}
          >
            <Box display="flex" alignItems="center" gap="12px">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#F0F1F1",
                  borderRadius: "7px",
                }}
              >
                <img
                  src={Images.hospitalGrey}
                  alt="hospital"
                  width={24}
                  height={24}
                />
              </Box>
              <Box>
                <Typography
                  fontSize="12px"
                  color="#686866"
                  fontWeight={500}
                  mb={0.5}
                >
                  Hospitalized at
                </Typography>
                <Typography fontWeight="600" color="#1D1D1B" fontSize="14px">
                  {hospitalAddress}
                </Typography>
              </Box>
            </Box>
            <Box
              component="img"
              src={Images.gps}
              alt="gps"
              width={20}
              height={20}
              onClick={handleHospitalLinkClick}
              sx={{
                cursor: hospitalLink ? "pointer" : "default",
                "&:hover": hospitalLink ? { opacity: 0.7 } : {},
              }}
            />
          </CardContent>
        </Card>
        )}
        </Box>
      </Box>
    </Box>

    {/* Share Dialog — same pattern as ReferFriend / LifeDetailPageV2's "Share now" */}
    <Dialog
      open={showShareDialog}
      onClose={handleCloseShareDialog}
      maxWidth={isMobile ? "xs" : "md"}
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          margin: isMobile ? "16px" : "auto",
          maxHeight: isMobile ? "calc(100vh - 32px)" : "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...(isMobile && { width: "calc(100% - 32px)", maxWidth: "100%" }),
        },
      }}
      sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}
    >
      <ShareLink
        shareData={shareInfo}
        fallbackUrl={fallbackShareUrl}
        isLoading={shareLoading}
        error={shareError}
        onRetry={fetchShareDetails}
        onClose={handleCloseShareDialog}
      />
    </Dialog>
    </>
  );
};

export default MedicalCampaignHeader;
